import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Grid, IconButton } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import DownloadIcon from "@mui/icons-material/Download";
import StopIcon from "@mui/icons-material/Stop";

const iconButtonSx = {
  color: "black",
  padding: "2px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
};

/**
 * Compact, transparent player (meant to sit in a toolbar strip) styled like
 * ClipAudioPlayer's compact mode, backed by a
 * native <audio> element. The ref exposes `audioEl` (like react-audio-player)
 * so callers can keep driving the element directly.
 */
const NativeClipAudioPlayer = forwardRef(
  (
    {
      src,
      volume = 1,
      onPlay,
      onPause,
      onEnded,
      timeDecimalPlaces = 3,
      className,
    },
    ref
  ) => {
    const audioRef = useRef(null);
    const animationRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useImperativeHandle(ref, () => ({ audioEl: audioRef }), []);

    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.volume = Math.min(Math.max(volume, 0), 1);
      }
    }, [volume]);

    // timeupdate fires only a few times a second, so poll while playing to
    // keep the millisecond display smooth.
    useEffect(() => {
      if (!isPlaying) {
        return undefined;
      }

      const tick = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
        animationRef.current = requestAnimationFrame(tick);
      };

      animationRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(animationRef.current);
    }, [isPlaying]);

    const handlePlayClick = () => {
      audioRef.current?.play?.()?.catch?.(() => {});
    };

    const handleStopClick = () => {
      const audio = audioRef.current;

      if (!audio) return;

      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
    };

    const handleDownload = async () => {
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = src.split("/").pop() || "audio.mp3";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Error downloading audio:", error);
      }
    };

    const syncTime = useCallback(() => {
      setCurrentTime(audioRef.current?.currentTime || 0);
    }, []);

    const formatTime = (time) => {
      const safeTime = Number.isFinite(time) ? time : 0;
      return safeTime.toFixed(Math.max(0, timeDecimalPlaces));
    };

    return (
      <Grid
        container
        item
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
        wrap="nowrap"
        className={className}
        style={{ height: "100%" }}
      >
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onPlay={() => {
            setIsPlaying(true);
            onPlay?.();
          }}
          onPause={() => {
            setIsPlaying(false);
            syncTime();
            onPause?.();
          }}
          onEnded={() => {
            setIsPlaying(false);
            syncTime();
            onEnded?.();
          }}
          onSeeked={syncTime}
          onTimeUpdate={isPlaying ? undefined : syncTime}
          onLoadedMetadata={() => {
            setDuration(audioRef.current?.duration || 0);
            syncTime();
          }}
          onEmptied={() => {
            setIsPlaying(false);
            setDuration(0);
            setCurrentTime(0);
          }}
        />

        <Grid item sx={{ display: "flex", alignItems: "center" }}>
          {isPlaying ? (
            <IconButton size="small" onClick={handleStopClick} sx={iconButtonSx}>
              <StopIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={handlePlayClick}
              disabled={!src}
              sx={iconButtonSx}
            >
              <PlayArrowRoundedIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          )}
        </Grid>

        <Grid item fontSize="14px" fontFamily="Livvic, sans-serif">
          <span>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </Grid>

        <Grid item sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            size="small"
            onClick={handleDownload}
            disabled={!src}
            sx={iconButtonSx}
          >
            <DownloadIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Grid>
      </Grid>
    );
  }
);

NativeClipAudioPlayer.displayName = "NativeClipAudioPlayer";

NativeClipAudioPlayer.propTypes = {
  src: PropTypes.string,
  volume: PropTypes.number,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  timeDecimalPlaces: PropTypes.number,
  className: PropTypes.string,
};

export default NativeClipAudioPlayer;
